/* eslint-disable @typescript-eslint/no-explicit-any */

type Key = string | number;
type CoreDictKey = keyof any;
export type KeyFn<T, TKey = Key> = (obj: T) => TKey;

/**
 * Essentially a sorted dictionary.
 * Has most of the functionality of Array, plus O[1] lookup
 * (at the expense of slower initialization and sorting)
 */
export abstract class TypedKeyedArray<
	TType extends TypedKeyedArray<TType, T, TKey>,
	T,
	TKey extends CoreDictKey = Key,
> {
	readonly data: T[];
	readonly keys = new Map<TKey, number>();
	readonly keyFn: KeyFn<T, TKey>;

	constructor(data: T[], keyFn: KeyFn<T, TKey>) {
		this.data = (data ?? []).map((d) => d);
		this.keyFn = (o: T) => o && keyFn(o);
		this.resetKeys();
	}

	protected abstract newArray(data: T[]): TType;

	/** Adds to list, or updates if exists */
	set(obj: T) {
		const key = this.keyFn(obj);
		const index = this.keys.get(key);
		if (!hasValue(index)) {
			this.setKey(key, this.data.length);
			this.data.push(obj);
		} else {
			this.data[index] = obj;
		}
	}

	protected setKey(key: TKey, index: number) {
		this.keys.set(key, index);
	}
	protected resetKeys() {
		this.data.map(this.keyFn).forEach(this.setKey.bind(this));
	}

	remove(item: T) {
		return this.removeId(this.keyFn(item));
	}

	removeId(id: TKey) {
		if (this.get(id)) {
			const index = this.keys.get(id);
			this.keys.delete(id);
			this.data.splice(index, 1);
			this.resetKeys();
			return true;
		}
		return false;
	}

	/** O[1] accessor (gets by id) */
	get(id: TKey) {
		return this.data[this.keys.get(id)];
	}

	/** O[1] accessor (gets by id) */
	has(id: TKey) {
		return hasValue(this.keys.get(id));
	}

	sort(compareFn: (a: T, b: T) => number) {
		const sortedData = this.data.map((d) => d).toSorted(compareFn);
		return this.newArray(sortedData);
	}

	map<TOut>(callback: (value: T, index: number, array: T[]) => TOut) {
		return this.data.map(callback);
	}

	filter(callback: (value: T, index: number, array: T[]) => boolean) {
		return this.newArray(this.data.filter(callback));
	}

	count() {
		return this.data?.length ?? 0;
	}

	any() {
		return this.count() > 0;
	}

	/** Returns copy of an array */
	copy() {
		return this.newArray(this.data.map(clone));
	}

	/** Operates like python, fetching an item based on index (negative indices are supported) */
	getAtIndex(index: number) {
		if (!this.any()) return null;
		if (index < 0) index = this.count() + index;
		return this.data[index];
	}

	first() {
		return this.getAtIndex(0);
	}

	last() {
		return this.getAtIndex(-1);
	}
}

export class KeyedArray<T, TKey extends CoreDictKey = Key> extends TypedKeyedArray<KeyedArray<T, TKey>, T, TKey> {
	protected newArray(data: T[]) {
		return new KeyedArray<T, TKey>(data, this.keyFn);
	}
}

export class KeyPairArray<TValue = any> extends TypedKeyedArray<KeyPairArray<TValue>, keyPair<TValue>> {
	constructor(data?: keyPair<TValue>[]) {
		super(data || [], (k) => k.id);
	}

	values() {
		return this.map((o) => o.value);
	}

	setValue(id: Key, value: TValue) {
		const pair = { id, value };
		super.set(pair);
	}

	getValue(id: Key) {
		const pair = this.get(id);
		return pair?.value;
	}

	/** Returns copy of array */
	copy() {
		return super.copy();
	}

	protected newArray(data: keyPair<TValue>[]) {
		return new KeyPairArray<TValue>(data);
	}
}

export interface keyPair<TValue = any> {
	id: Key;
	value: TValue;
}

export function toKeyPair<TValue>(id: number, value: TValue) {
	return { id, value } as keyPair<TValue>;
}

export function uniqueKeyPairs<TValue>(kpa: keyPair<TValue>[]) {
	return [...new Set(kpa.map((obj) => obj.id))].map((id) => kpa.find((obj) => obj.id === id));
}

/**
 * Maps object (which should be a dictionary-like) to a key pair array
 * @param obj
 * @returns
 */
export function objToKeyPairs(obj: object) {
	const kvps = Object.keys(obj).map((k) => {
		return { id: k, value: obj[k] };
	});
	return new KeyPairArray(kvps);
}

export class KeyPairArrayGeneric<TId extends Key, TValue> extends TypedKeyedArray<
	KeyPairArrayGeneric<TId, TValue>,
	{ id: TId; value: TValue }
> {
	constructor(data: { id: TId; value: TValue }[]) {
		super(data, (k) => k.id);
	}

	getValue(id: Key) {
		const pair = this.get(id);
		return pair?.value;
	}

	protected newArray(data: { id: TId; value: TValue }[]) {
		return new KeyPairArrayGeneric(data);
	}

	/** Returns copy of an array */
	copy() {
		return super.copy();
	}
}

export class KeyedSetArray<T> extends KeyPairArray<Set<T>> {
	constructor(data: keyPair<Set<T>>[] = []) {
		super(data);
	}

	addElement(id: Key, item: T) {
		const set = this.get(id)?.value ?? new Set<T>();
		set.add(item);
		super.setValue(id, set);
	}

	removeElement(id: Key, item: T) {
		const set = this.get(id)?.value ?? new Set<T>();
		set.delete(item);
		if (set.size > 0) super.setValue(id, set);
		else super.removeId(id);
	}

	getValue(id: Key) {
		const val = super.getValue(id);
		return val ?? new Set<T>();
	}

	hasItem(id: Key, item: T) {
		const set = this.getValue(id);
		return set.has(item);
	}
}

// TODO: This is a temp hack, we don't really wanna have these be reused but we don't want circular dependencies either
function hasValue(i: any) {
	return i !== undefined && i !== null;
}

function clone<T>(obj: T) {
	return obj && (JSON.parse(JSON.stringify(obj)) as T);
}
